// Task time (WOS-BUNDLE; Work OS `components/time-tracker.tsx`, `domain/time.ts`): one running
// timer per person. The task drawer's Time section starts and stops it with a live clock, books
// time by hand (minutes and a note), lists the entries (who, when, how long, approval, a hover
// delete on one's own) with the total, and shows digital employees' run time read-only. A list
// row carries a play / stop button (shown on hover; always on the running row); the top bar
// carries the running timer as a chip (task, clock, stop; a click opens the task). A board's
// header shows its booked total. Starting a timer stops the one running elsewhere (the server
// books it); a stop books at least a minute.
import {
  Badge,
  Button,
  cn,
  DrawerSection,
  Input,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BotIcon, ClockIcon, PlayIcon, PlusIcon, SquareIcon, Trash2Icon } from "lucide-react";
import { type FormEvent, useEffect, useId, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { type CompanyAccess, isManager } from "../company";
import { formatDate } from "../format";
import { problemText } from "../settings/problem-text";
import { useSession } from "../use-feature";
import {
  boardTimeQuery,
  bundleKeys,
  elapsedSince,
  formatDuration,
  formatElapsed,
  type TimerView,
  taskTimeQuery,
  timerQuery,
} from "./bundle-queries";
import { useSeatPeople } from "./model";

/** The current time, every second while `on` (a running clock). */
export function useTicker(on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [on]);
  return now;
}

/** The caller's timer and its writes; each refreshes the time it moved (tasks, boards). */
export function useTimer(api: Api, company: CompanyAccess) {
  const co = company.id;
  const qc = useQueryClient();
  const timer = useQuery(timerQuery(api, co));
  const refresh = (...taskIds: (string | undefined)[]) => {
    for (const id of taskIds)
      if (id) void qc.invalidateQueries({ queryKey: bundleKeys.taskTime(co, id) });
    void qc.invalidateQueries({ queryKey: ["company", co, "work", "board"], exact: false });
  };
  const start = useMutation({
    mutationFn: (taskId: string) => api.client.timer.start({ params: { co, id: taskId } }),
    onSuccess: (t) => {
      const was = qc.getQueryData<TimerView | null>(bundleKeys.timer(co));
      qc.setQueryData(bundleKeys.timer(co), t);
      refresh(t.task_id, was?.task_id);
      toast.add({
        title: was && was.task_id !== t.task_id ? `Timer moved to #${t.task_number}` : "Timer on",
        description:
          was && was.task_id !== t.task_id ? `#${was.task_number} was booked` : t.task_title,
      });
    },
    onError: (e) => toast.add({ title: problemText(e, "The timer did not start."), type: "error" }),
  });
  const stop = useMutation({
    mutationFn: () => api.client.timer.stop({ params: { co }, body: {} }),
    onSuccess: (r) => {
      const was = qc.getQueryData<TimerView | null>(bundleKeys.timer(co));
      qc.setQueryData(bundleKeys.timer(co), null);
      refresh(was?.task_id, r.time_entry?.task_id ?? undefined);
      if (r.time_entry)
        toast.add({
          title: `${formatDuration(r.time_entry.duration_seconds)} booked`,
          description: was?.task_title,
          type: "success",
        });
    },
    onError: (e) => toast.add({ title: problemText(e, "The timer did not stop."), type: "error" }),
  });
  return {
    running: timer.data ?? null,
    start: (taskId: string) => start.mutate(taskId),
    stop: () => stop.mutate(),
    pending: start.isPending || stop.isPending,
  };
}

/** A list row's play / stop button: hover-revealed, always shown on the running row. */
export function RowTimerButton({
  timer,
  taskId,
  title,
}: {
  timer: ReturnType<typeof useTimer>;
  taskId: string;
  title: string;
}) {
  const on = timer.running?.task_id === taskId;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-xs"
            data-slot="row-timer"
            data-running={on || undefined}
            aria-pressed={on}
            aria-label={on ? `Stop the timer on ${title}` : `Start a timer on ${title}`}
            disabled={timer.pending}
            onClick={() => (on ? timer.stop() : timer.start(taskId))}
            className={cn(
              "opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100",
              on && "text-danger-text opacity-100",
            )}
          />
        }
      >
        {on ? (
          <SquareIcon aria-hidden="true" className="fill-current" />
        ) : (
          <PlayIcon aria-hidden="true" />
        )}
      </TooltipTrigger>
      <TooltipContent>{on ? "Stop timer" : "Start timer"}</TooltipContent>
    </Tooltip>
  );
}

/** The top bar's running timer: the task, its clock and Stop; a click opens the task. */
export function TimerChip({ api, company }: { api: Api; company: CompanyAccess }) {
  const timer = useTimer(api, company);
  const t = timer.running;
  const now = useTicker(Boolean(t));
  if (!t) return null;
  return (
    <div
      data-slot="timer-chip"
      className="flex h-7 min-w-0 items-center gap-1 rounded-full border border-border bg-card pr-0.5 pl-2 text-xs shadow-xs"
    >
      <span aria-hidden="true" className="size-1.5 shrink-0 animate-pulse rounded-full bg-danger" />
      <Link
        to="/c/$slug/work/b/$boardId"
        params={{ slug: company.slug, boardId: t.board_id }}
        search={{ ...keepFocus, task: t.task_id }}
        className="flex min-w-0 items-center gap-1.5 rounded-xs hover:text-foreground"
        aria-label={`Open #${t.task_number} ${t.task_title}, timer running ${formatElapsed(elapsedSince(t.started_at, now))}`}
      >
        <span className="max-w-40 truncate text-secondary max-lg:hidden">{t.task_title}</span>
        <span className="font-medium tabular-nums text-foreground" aria-hidden="true">
          {formatElapsed(elapsedSince(t.started_at, now))}
        </span>
      </Link>
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-xs"
              className="rounded-full text-danger-text"
              aria-label="Stop the timer"
              disabled={timer.pending}
              onClick={timer.stop}
            />
          }
        >
          <SquareIcon aria-hidden="true" className="fill-current" />
        </TooltipTrigger>
        <TooltipContent>Stop and book the time</TooltipContent>
      </Tooltip>
    </div>
  );
}

/** The board header's booked time ("12 h 30 min tracked"), with agents' time in the tip. */
export function BoardTimeTotal({
  api,
  company,
  boardId,
}: {
  api: Api;
  company: CompanyAccess;
  boardId: string;
}) {
  const q = useQuery(boardTimeQuery(api, company.id, boardId));
  if (!q.data || (!q.data.total_seconds && !q.data.agent_seconds)) return null;
  const tip = `${formatDuration(q.data.total_seconds)} booked by people${
    q.data.agent_seconds ? ` · ${formatDuration(q.data.agent_seconds)} agent run time` : ""
  }`;
  return (
    <span data-slot="board-time" className="flex items-center gap-1 tabular-nums" title={tip}>
      <ClockIcon aria-hidden="true" className="size-3.5" />
      <span className="sr-only">Time tracked: </span>
      {formatDuration(q.data.total_seconds)}
    </span>
  );
}

/** The drawer's Time section. */
export function TimeSection({
  api,
  company,
  taskId,
}: {
  api: Api;
  company: CompanyAccess;
  taskId: string;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const q = useQuery(taskTimeQuery(api, co, taskId));
  const timer = useTimer(api, company);
  const people = useSeatPeople(api, co);
  const { me } = useSession();
  const myMembership = me.memberships.find((m) => m.company_id === co)?.membership_id ?? null;
  const manager = isManager(company);
  const running = timer.running?.task_id === taskId ? timer.running : null;
  const now = useTicker(Boolean(running));
  const [adding, setAdding] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: bundleKeys.taskTime(co, taskId) });
    void qc.invalidateQueries({ queryKey: ["company", co, "work", "board"], exact: false });
  };
  const remove = useMutation({
    mutationFn: (id: string) =>
      api.client.tasks.deleteTime({ params: { co, id: taskId, item_id: id } }),
    onSuccess: () => {
      refresh();
      toast.add({ title: "Time entry removed" });
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The entry was not removed."), type: "error" }),
  });
  const time = q.data;
  if (q.isSuccess && !time) return null; // The API does not serve task time yet.
  const entries = time?.entries ?? [];
  return (
    <DrawerSection
      data-slot="task-time"
      title={
        <span className="flex items-center gap-2">
          Time
          {time && (time.total_seconds || running) ? (
            <span className="text-xs font-normal text-muted-foreground tabular-nums">
              {formatDuration(time.total_seconds)} total
            </span>
          ) : null}
        </span>
      }
      action={
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            aria-expanded={adding}
            onClick={() => setAdding((v) => !v)}
          >
            <PlusIcon aria-hidden="true" />
            Log time
          </Button>
          <Button
            variant={running ? "destructive" : "secondary"}
            size="sm"
            data-slot="drawer-timer"
            aria-pressed={Boolean(running)}
            disabled={timer.pending}
            onClick={() => (running ? timer.stop() : timer.start(taskId))}
          >
            {running ? (
              <SquareIcon aria-hidden="true" className="fill-current" />
            ) : (
              <PlayIcon aria-hidden="true" />
            )}
            {running ? (
              <>
                Stop{" "}
                <span className="tabular-nums">
                  {formatElapsed(elapsedSince(running.started_at, now))}
                </span>
              </>
            ) : (
              "Start timer"
            )}
          </Button>
        </div>
      }
    >
      {adding ? (
        <LogTimeForm
          api={api}
          co={co}
          taskId={taskId}
          onDone={() => {
            setAdding(false);
            refresh();
          }}
        />
      ) : null}
      {entries.length ? (
        <ul aria-label="Time entries" className="flex flex-col">
          {entries.map((e) => {
            const who = people.nameOf(e.membership_id).name;
            const mine = e.membership_id === myMembership;
            return (
              <li
                key={e.id}
                data-time-entry={e.id}
                className="group/entry flex min-h-8 items-center gap-2 rounded-md px-2 text-sm hover:bg-sunken"
              >
                <span className="w-16 shrink-0 font-medium tabular-nums">
                  {formatDuration(e.duration_seconds)}
                </span>
                <span className="min-w-0 flex-1 truncate text-secondary">
                  {who}
                  <span className="text-muted-foreground"> · {formatDate(e.created_at)}</span>
                  {e.note ? <span className="text-muted-foreground"> · {e.note}</span> : null}
                </span>
                {e.source === "timer" ? (
                  <span className="text-xs text-muted-foreground max-sm:hidden">timer</span>
                ) : null}
                {e.approval_status !== "approved" ? (
                  <Badge variant={e.approval_status === "rejected" ? "danger" : "warning"}>
                    {e.approval_status === "rejected" ? "Rejected" : "Pending approval"}
                  </Badge>
                ) : null}
                {mine || manager ? (
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          className="opacity-0 group-hover/entry:opacity-100 group-focus-within/entry:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100 hover:text-danger-text"
                          aria-label={`Remove ${formatDuration(e.duration_seconds)} by ${who}`}
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(e.id)}
                        />
                      }
                    >
                      <Trash2Icon aria-hidden="true" />
                    </TooltipTrigger>
                    <TooltipContent>Remove entry</TooltipContent>
                  </Tooltip>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : !adding && !running ? (
        <p className="text-sm text-muted-foreground">
          No time booked yet. Start the timer, or log time by hand.
        </p>
      ) : null}
      {time?.agent_seconds ? (
        <p
          data-slot="agent-time"
          className="mt-2 flex items-center gap-1.5 px-2 text-xs text-muted-foreground"
        >
          <BotIcon aria-hidden="true" className="size-3.5" />
          Agent time: {formatDuration(time.agent_seconds)} of run time (read-only)
        </p>
      ) : null}
    </DrawerSection>
  );
}

function LogTimeForm({
  api,
  co,
  taskId,
  onDone,
}: {
  api: Api;
  co: string;
  taskId: string;
  onDone: () => void;
}) {
  const id = useId();
  const [minutes, setMinutes] = useState("");
  const [note, setNote] = useState("");
  const n = Number(minutes);
  const ok = Number.isInteger(n) && n >= 1 && n <= 24 * 60;
  const log = useMutation({
    mutationFn: () =>
      api.client.tasks.logTime({
        params: { co, id: taskId },
        body: { minutes: n, ...(note.trim() ? { note: note.trim() } : {}) },
      }),
    onSuccess: (e) => {
      toast.add({ title: `${formatDuration(e.duration_seconds)} booked`, type: "success" });
      onDone();
    },
    onError: (e) => toast.add({ title: problemText(e, "The time was not booked."), type: "error" }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ok) log.mutate();
  };
  return (
    <form
      onSubmit={submit}
      aria-label="Log time"
      className="mb-2 flex flex-wrap items-center gap-2 rounded-md border border-border-subtle bg-sunken p-2"
    >
      <label htmlFor={`${id}-min`} className="sr-only">
        Minutes
      </label>
      <Input
        id={`${id}-min`}
        autoFocus
        inputMode="numeric"
        placeholder="Minutes"
        className="h-8 w-24"
        value={minutes}
        onChange={(e) => setMinutes(e.target.value.replace(/\D/g, ""))}
      />
      <label htmlFor={`${id}-note`} className="sr-only">
        Note
      </label>
      <Input
        id={`${id}-note`}
        placeholder="Note (optional)"
        className="h-8 min-w-0 flex-1"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <Button type="submit" size="sm" disabled={!ok || log.isPending}>
        Book
      </Button>
    </form>
  );
}
